import { get, post, put, del } from '../../apiClient';
import { API_ENDPOINTS } from '../../config';

const targetService = {
  /**
   * Create a new target
   */
  createTarget: async (targetData) => {
    try {
      const response = await post(API_ENDPOINTS.CRM.TARGETS, targetData);

      if (response && response.responseCode === 201) {
        return {
          success: true,
          target: response.data
        };
      } else {
        // Extract errors object if available (handles HTTP 200 with responseCode 400 case)
        const errors = response.errors || {};
        const errorMessage = response.responseMessage || 'Failed to create target';

        return {
          success: false,
          error: errorMessage,
          errors: errors // Include detailed errors object
        };
      }
    } catch (error) {

      // Try to extract errors from multiple possible sources
      let errors = {};
      let errorMessage = error.message || 'Failed to create target';

      // Priority 1: Check if error object has errors property (attached by apiClient interceptor)
      if (error.errors && Object.keys(error.errors).length > 0) {
        errors = error.errors;
        if (error.responseData?.responseMessage) {
          errorMessage = error.responseData.responseMessage;
        }
      }
      // Priority 2: Check responseData.errors (also attached by interceptor)
      else if (error.responseData?.errors && Object.keys(error.responseData.errors).length > 0) {
        errors = error.responseData.errors;
        if (error.responseData.responseMessage) {
          errorMessage = error.responseData.responseMessage;
        }
      }
      // Priority 3: Check if error has response data (axios error before interceptor)
      else if (error.response?.data) {
        errors = error.response.data.errors || {};
        errorMessage = error.response.data.responseMessage || errorMessage;
      }


      return {
        success: false,
        error: errorMessage,
        errors: errors
      };
    }
  },

  /**
   * Get all targets with filters and pagination
   */
  getAllTargets: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.period && filters.period !== 'all') {
        queryParams.append('period', filters.period);
      }
      if (filters.type && filters.type !== 'all') {
        queryParams.append('type', filters.type);
      }
      if (filters.scope && filters.scope !== 'all') {
        queryParams.append('scope', filters.scope);
      }
      if (filters.status && filters.status !== 'all') {
        queryParams.append('status', filters.status);
      }
      if (filters.assignee && filters.assignee !== 'all') {
        queryParams.append('assignee', filters.assignee);
      }
      if (filters.search && filters.search.trim()) {
        queryParams.append('search', filters.search.trim());
      }
      if (filters.range && filters.range !== 'all_time') {
        queryParams.append('range', filters.range);
      }
      if (filters.startDate) {
        queryParams.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        queryParams.append('endDate', filters.endDate);
      }
      // Pagination parameters
      if (filters.page) {
        queryParams.append('page', filters.page.toString());
      }
      if (filters.limit) {
        queryParams.append('limit', filters.limit.toString());
      }

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.TARGETS}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.TARGETS;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        // Handle paginated response
        if (response.data && response.data.targets && response.data.pagination) {
          return {
            success: true,
            targets: Array.isArray(response.data.targets) ? response.data.targets : [],
            total: response.data.pagination.totalItems || 0,
            pagination: response.data.pagination
          };
        }
        // Fallback for non-paginated response
        const targets = Array.isArray(response.data) ? response.data : [];
        return {
          success: true,
          targets: targets,
          total: targets.length,
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalItems: targets.length,
            itemsPerPage: targets.length,
            hasNextPage: false,
            hasPrevPage: false
          }
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve targets',
          targets: [],
          total: 0
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to retrieve targets',
        targets: [],
        total: 0
      };
    }
  },

  /**
   * Get target by ID
   */
  getTargetById: async (targetId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.TARGETS}/${targetId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          target: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve target'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to retrieve target'
      };
    }
  },

  /**
   * Export a single target detail as a backend-generated Excel (.xlsx)
   * workbook and trigger the browser download.
   */
  exportTargetDetail: async (targetId, fallbackName = 'Target') => {
    try {
      if (!targetId) {
        return { success: false, error: 'Target ID is required' };
      }

      const blob = await get(`${API_ENDPOINTS.CRM.TARGETS}/${targetId}/export`, {
        responseType: 'blob'
      });

      const sanitized = String(fallbackName || 'Target')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80) || 'Target';
      const filename = `Target_${sanitized}.xlsx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export target details'
      };
    }
  },

  /**
   * Update target
   */
  updateTarget: async (targetId, targetData) => {
    try {
      const response = await put(`${API_ENDPOINTS.CRM.TARGETS}/${targetId}`, targetData);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          target: response.data
        };
      } else {
        // Extract errors object if available
        const errors = response.errors || {};
        const errorMessage = response.responseMessage || 'Failed to update target';

        return {
          success: false,
          error: errorMessage,
          errors: errors // Include detailed errors object
        };
      }
    } catch (error) {
      
      // Try to extract errors from multiple possible sources
      let errors = {};
      let errorMessage = error.message || 'Failed to update target';

      // Priority 1: Check if error object has errors property (attached by apiClient interceptor)
      if (error.errors) {
        errors = error.errors;
        if (error.responseData?.responseMessage) {
          errorMessage = error.responseData.responseMessage;
        }
      }
      // Priority 2: Check if error has response data (axios error before interceptor)
      else if (error.response?.data) {
        errors = error.response.data.errors || {};
        errorMessage = error.response.data.responseMessage || errorMessage;
      }

      return {
        success: false,
        error: errorMessage,
        errors: errors
      };
    }
  },

  /**
   * Delete target
   */
  deleteTarget: async (targetId) => {
    try {
      const response = await del(`${API_ENDPOINTS.CRM.TARGETS}/${targetId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete target'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to delete target'
      };
    }
  },

  /**
   * Get dashboard data
   */
  getDashboard: async () => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.TARGETS}/dashboard`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve dashboard data'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to retrieve dashboard data'
      };
    }
  },

  /**
   * Export targets
   */
  exportTargets: async (format = 'csv') => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.TARGETS}/export`, {
        params: { format }
      });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to export targets'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to export targets'
      };
    }
  },

  /**
   * Get opportunities for a target (drill-down)
   * Only for "New Opportunities" target type
   */
  getTargetOpportunities: async (targetId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.TARGETS}/${targetId}/opportunities`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          opportunities: response.data?.opportunities || [],
          count: response.data?.count || 0,
          target: response.data?.target
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve opportunities',
          opportunities: [],
          count: 0
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to retrieve opportunities',
        opportunities: [],
        count: 0
      };
    }
  },

  /**
   * Get revenue quotations for a target (drill-down)
   * Only for "Revenue" target type
   */
  getTargetRevenueQuotations: async (targetId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.TARGETS}/${targetId}/revenue-quotations`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          quotations: response.data?.quotations || [],
          count: response.data?.count || 0,
          target: response.data?.target
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve revenue quotations',
          quotations: [],
          count: 0
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to retrieve revenue quotations',
        quotations: [],
        count: 0
      };
    }
  },

  /**
   * Get deals (opportunities + accepted quotations) for a target (drill-down)
   * Only for "Deal Count" target type
   */
  getTargetDeals: async (targetId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.TARGETS}/${targetId}/deals`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          deals: response.data?.deals || [],
          count: response.data?.count || 0,
          target: response.data?.target
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve deals',
          deals: [],
          count: 0
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to retrieve deals',
        deals: [],
        count: 0
      };
    }
  },
  /**
   * Delete multiple targets
   */
  bulkDeleteTargets: async (targetIds) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.TARGETS}/bulk-delete`, {
        targetIds
      });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage || 'Targets deleted successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete targets'
        };
      }
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to delete targets'
      };
    }
  },

  /**
   * Preview bulk targets from CSV data (backend validation)
   */
  bulkPreview: async (targets) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.TARGETS}/bulk-preview`, { targets });
      if (response && response.responseCode === 200) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || 'Failed to generate preview' };
    } catch (error) {

      return { success: false, error: error.message || 'Failed to generate preview' };
    }
  },

  /**
   * Confirm and upload validated bulk targets
   */
  bulkUpload: async (targets) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.TARGETS}/bulk-upload`, { targets });
      if (response && response.responseCode === 201) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || 'Failed to upload targets' };
    } catch (error) {

      return { success: false, error: error.message || 'Failed to upload targets' };
    }
  }
};

export default targetService;
